import {
  addJournalPhoto,
  formatMoney,
  getJournal,
  getShareScreen,
  journalBookAddress,
  NotFound,
  Pick,
  removeHere,
  removeJournalPhoto,
  sayHere,
  setShareChoices,
  writeJournal,
  type Journal as Kept,
  type JournalDay,
  type ShareScreen,
  type TripWeather,
} from '@trippath/shared';
import { format, parseISO } from 'date-fns';
import { enGB, et } from 'date-fns/locale';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { useLive } from '../../app/live';
import { MapPanel } from '../../components/map/MapPanel';
import { SkyIcon } from '../../components/weather/SkyIcon';
import { degrees } from '../../components/weather/StopWeather';
import { readyWeather, useWeather } from '../../components/weather/useWeather';
import { day as dayLong } from '../../lib/format';
import { dayRange } from '../../lib/countryFacts';
import type { MapData } from '../../lib/map';
import { useReadOnly } from '../../app/readOnly';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; journal: Kept };

/** Today on this device's clock, `2027-03-10`. */
function todayHere(now: Date): string {
  return format(now, 'yyyy-MM-dd');
}

/** A day as the mockup heads it: `10.` over `kolmapäev märts`. */
function when(iso: string, language: string): { number: string; words: string } {
  const locale = language === 'et' ? et : enGB;
  const date = parseISO(iso);
  return {
    number: format(date, 'd.', { locale }),
    words: format(date, 'EEEE MMMM', { locale }),
  };
}

/** The weather of a day of a stop, when it is known. */
function skyOn(weather: TripWeather | null, day: JournalDay) {
  const found = weather?.stops
    .find((one) => one.stop === day.stop)
    ?.days.find((one) => one.day === day.day);
  return found?.kind ? found : null;
}

/** The days of the journal by stop, the latest first; only those come. Each stop from its first
 * day come to its last. */
function byStop(days: JournalDay[], today: string) {
  const come = days.filter((day) => day.day <= today).reverse();
  const groups: {
    stop: string | null;
    city: string | null;
    from: string;
    to: string;
    days: JournalDay[];
  }[] = [];
  for (const day of come) {
    const last = groups.at(-1);
    if (last?.stop === day.stop) {
      last.days.push(day);
      last.from = day.day;
    } else {
      groups.push({ stop: day.stop, city: day.city, from: day.day, to: day.day, days: [day] });
    }
  }
  return groups;
}

/** Photos picked from the phone and added to a day: each of the day its camera said when that is
 * one of the trip's. */
function AddPhotos({
  trip,
  day,
  busy,
  onAdd,
}: {
  trip: string;
  day: string;
  busy: boolean;
  onAdd: (job: Promise<unknown>) => void;
}) {
  const { t } = useTranslation();
  const files = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={files}
        type="file"
        accept="image/*"
        multiple
        hidden
        aria-label={t('journal.addPhotos')}
        onChange={(event) => {
          const picked = [...(event.target.files as FileList)];
          event.target.value = '';
          onAdd(Promise.all(picked.map((file) => addJournalPhoto(trip, day, file))));
        }}
      />
      <button
        type="button"
        className="button button-small"
        disabled={busy}
        onClick={() => files.current?.click()}
      >
        {t('journal.addPhotos')}
      </button>
    </>
  );
}

/** A day of the journal: written or not, its photos, its facts, and what can be done with it. */
function DayCard({
  trip,
  day,
  sky,
  today,
  onChanged,
}: {
  trip: string;
  day: JournalDay;
  sky: ReturnType<typeof skyOn>;
  today: boolean;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  // A trip read only: its journal read, nothing written, added or taken off.
  const locked = useReadOnly();
  const lang = i18n.language;
  const ids = useId();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [failed, setFailed] = useState<'save' | 'here' | null>(null);
  const [busy, setBusy] = useState(false);
  const head = when(day.day, lang);
  const done = (job: Promise<unknown>, why: 'save' | 'here' = 'save') => {
    setBusy(true);
    setFailed(null);
    job.then(
      () => {
        setBusy(false);
        setEditing(false);
        onChanged();
      },
      () => {
        setBusy(false);
        setFailed(why);
      },
    );
  };
  const edit = () => {
    setTitle(day.entry?.title ?? '');
    setBody(day.entry?.body ?? '');
    setEditing(true);
  };
  const here = () => {
    navigator.geolocation.getCurrentPosition(
      (found) => {
        done(
          sayHere(trip, day.day, {
            at: format(new Date(found.timestamp), 'HH:mm'),
            lat: found.coords.latitude,
            lon: found.coords.longitude,
          }),
          'here',
        );
      },
      () => {
        setFailed('here');
      },
    );
  };
  const places = new Set(day.photos.flatMap((photo) => (photo.place ? [photo.place] : [])))
    .size;
  const facts = [
    sky && `${degrees(sky.high)} / ${degrees(sky.low)}`,
    places > 0 && t('journal.places', { count: places }),
    day.ways.length > 0 &&
      day.ways
        .map((way) => t('journal.way', { how: t(`city.by.${way.by}`), minutes: way.minutes }))
        .join(', '),
    day.spent && t('journal.spent', { amount: formatMoney(day.spent, lang) }),
  ].filter((fact): fact is string => typeof fact === 'string');
  return (
    <article className="journal-day" aria-labelledby={`${ids}-day`}>
      <div className="journal-when" id={`${ids}-day`}>
        <b>{head.number}</b>
        <span>{head.words}</span>
      </div>
      <div className="journal-text">
        {editing ? (
          <form
            className="journal-form"
            onSubmit={(event) => {
              event.preventDefault();
              done(writeJournal(trip, day.day, title, body));
            }}
          >
            <div className="field">
              <label className="field-label" htmlFor={`${ids}-title`}>
                {t('journal.titleField')}
              </label>
              <input
                id={`${ids}-title`}
                className="text-input"
                maxLength={200}
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                }}
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor={`${ids}-body`}>
                {t('journal.bodyField')}
              </label>
              <textarea
                id={`${ids}-body`}
                className="text-input"
                rows={6}
                maxLength={20000}
                value={body}
                onChange={(event) => {
                  setBody(event.target.value);
                }}
              />
            </div>
            <div className="journal-actions">
              <button
                type="submit"
                className="button button-primary button-small"
                disabled={busy}
              >
                {t('journal.save')}
              </button>
              <button
                type="button"
                className="button button-small"
                onClick={() => {
                  setEditing(false);
                }}
              >
                {t('journal.cancel')}
              </button>
            </div>
          </form>
        ) : day.entry ? (
          <>
            {day.entry.title !== '' && <h3>{day.entry.title}</h3>}
            {day.entry.body.split('\n').map((line, at) => (
              <p key={`${String(at)} ${line}`}>{line}</p>
            ))}
          </>
        ) : (
          <>
            <h3 className="quiet">{t('journal.unwritten')}</h3>
            <p className="small quiet">
              {day.plan.length > 0 && t('journal.fromPlan', { things: day.plan.join(', ') })}{' '}
              {day.photos.length > 0 && t('journal.photos', { count: day.photos.length })}
            </p>
          </>
        )}
        {day.photos.length > 0 && (
          <ul className="journal-photos">
            {day.photos.map((photo) => {
              const said =
                photo.at && photo.place
                  ? t('journal.photoAt', { at: photo.at, place: photo.place })
                  : t('journal.photo');
              return (
                <li key={photo.id}>
                  <img src={photo.image} alt={said} loading="lazy" />
                  {photo.at && (
                    <span>
                      {photo.at}
                      {photo.place && ` · ${photo.place}`}
                    </span>
                  )}
                  {!locked && (
                    <button
                      type="button"
                      className="journal-remove"
                      aria-label={t('journal.remove', { name: said })}
                      onClick={() => {
                        done(removeJournalPhoto(trip, photo.id));
                      }}
                    >
                      ×
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {day.here.length > 0 && (
          <ul className="journal-moments">
            {day.here.map((one) => (
              <li key={one.id}>
                <span>
                  {one.at} · {one.place}
                </span>
                {!locked && (
                  <button
                    type="button"
                    className="link-button"
                    aria-label={t('journal.remove', { name: `${one.at} · ${one.place}` })}
                    onClick={() => {
                      done(removeHere(trip, one.id));
                    }}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {facts.length > 0 && (
          <p className="journal-facts">
            {sky && <SkyIcon sky={sky.sky} size={14} label={t(`weather.sky.${sky.sky}`)} />}
            {facts.join(' · ')}
          </p>
        )}
        {failed && (
          <p className="form-failed" role="alert">
            {t(failed === 'here' ? 'journal.hereFailed' : 'journal.failed')}
          </p>
        )}
        {!editing && !locked && (
          <div className="journal-actions">
            <button type="button" className="button button-small" onClick={edit}>
              {t(day.entry ? 'journal.edit' : 'journal.write')}
            </button>
            <AddPhotos trip={trip} day={day.day} busy={busy} onAdd={done} />
            {today && (
              <button type="button" className="button button-small" onClick={here}>
                {t('journal.here')}
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

/** Sharing the journal by the trip's read-only link, and its book. */
function ShareAndBook({ trip }: { trip: string }) {
  const { t, i18n } = useTranslation();
  const locked = useReadOnly();
  const ids = useId();
  const [screen, setScreen] = useState<ShareScreen | null>(null);
  const [paper, setPaper] = useState<'a5' | 'a4'>('a5');
  const [bleed, setBleed] = useState(true);
  useEffect(() => {
    // Only the owner has the link's screen; another sees the book alone.
    getShareScreen(trip).then(setScreen, () => undefined);
  }, [trip]);
  return (
    <section className="panel" aria-labelledby={`${ids}-share`}>
      <div className="panel-head">
        <h2 id={`${ids}-share`}>{t('journal.shareTitle')}</h2>
      </div>
      <div className="panel-body journal-share">
        {screen &&
          (screen.on ? (
            <label className="check">
              <input
                type="checkbox"
                checked={screen.journal}
                disabled={locked}
                onChange={() => {
                  // Ticked at once, as the server is told; back as it was if it says no.
                  const was = screen;
                  setScreen({ ...was, journal: !was.journal });
                  setShareChoices(trip, {
                    expires: was.expires,
                    phrase: was.phrase !== null,
                    amounts: was.amounts,
                    files: was.files,
                    names: was.names,
                    journal: !was.journal,
                  }).then(setScreen, () => {
                    setScreen(was);
                  });
                }}
              />
              {t('journal.share')}
            </label>
          ) : (
            <p className="small">
              <Link to={`/trips/${trip}/sharing`}>{t('journal.noLink')}</Link>
            </p>
          ))}
        <p className="small">{t('journal.bookLead')}</p>
        <Pick
          label={t('journal.paper')}
          options={[
            { value: 'a5', label: 'A5' },
            { value: 'a4', label: 'A4' },
          ]}
          value={paper}
          onChange={(value) => {
            setPaper(value);
          }}
        />
        <label className="check">
          <input
            type="checkbox"
            checked={bleed}
            onChange={() => {
              setBleed(!bleed);
            }}
          />
          {t('journal.bleed')}
        </label>
        <a
          className="button button-small"
          href={journalBookAddress(trip, paper, bleed, i18n.language)}
          download
        >
          {t('journal.book')}
        </a>
      </div>
    </section>
  );
}

/**
 * A trip's journal (the `Journal` mockup): its days come so far, the latest first, by stop —
 * each written or not, its photos placed by their camera or the plan, a place said by "I am
 * here", the day's weather, places, ways and spends; the photos' route on the map; how places
 * are known without tracking; the journal on the read-only link, and its book.
 */
export function Journal() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { id = '' } = useParams();
  const { version } = useLive();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [asked, setAsked] = useState(0);
  const [today] = useState(() => todayHere(new Date()));
  const weather = readyWeather(useWeather(id));

  useEffect(() => {
    let live = true;
    getJournal(id).then(
      (journal) => {
        if (live) setState({ kind: 'ready', journal });
      },
      (error: unknown) => {
        if (live) setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
    return () => {
      live = false;
    };
  }, [id, asked, version]);

  const data = useMemo((): MapData => {
    if (state.kind !== 'ready') return { points: [], lines: [] };
    const seen = state.journal.days.flatMap((day) =>
      [
        ...day.photos.flatMap((photo) =>
          photo.lat === null || photo.lon === null
            ? []
            : [
                {
                  at: `${day.day} ${photo.at ?? ''}`,
                  lat: photo.lat,
                  lon: photo.lon,
                  label: photo.place ?? '',
                },
              ],
        ),
        ...day.here.map((one) => ({
          at: `${day.day} ${one.at}`,
          lat: one.lat,
          lon: one.lon,
          label: one.place,
        })),
      ].sort((a, b) => a.at.localeCompare(b.at)),
    );
    return {
      points: seen.map((one) => ({
        lat: one.lat,
        lon: one.lon,
        label: one.label,
        kind: 'stop',
      })),
      lines:
        seen.length > 1
          ? [{ kind: 'foot', path: seen.map((one): [number, number] => [one.lon, one.lat]) }]
          : [],
    };
  }, [state]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }
  const { journal } = state;
  const first = journal.days[0]?.day ?? today;
  const changed = () => {
    setAsked((was) => was + 1);
  };
  return (
    <div className="page journal-page">
      <div className="page-head">
        <div>
          <h1>{t('journal.title')}</h1>
          <p className="lead quiet">{t('journal.lead')}</p>
        </div>
      </div>
      {!journal.started ? (
        <p className="panel panel-body">{t('journal.notYet', { day: dayLong(first, lang) })}</p>
      ) : (
        <div className="journal-layout">
          <div className="journal-stops">
            {byStop(journal.days, today).map((group) => {
              const { from, to } = group;
              return (
                <section key={`${String(group.stop)} ${from}`} className="panel">
                  <div className="panel-head">
                    <h2>
                      {t('journal.stop', {
                        city: group.city ?? '',
                        dates: dayRange(from, to, lang),
                      })}
                    </h2>
                    <div className="journal-head-actions">
                      <AddPhotos
                        trip={id}
                        day={to}
                        busy={false}
                        onAdd={(job) => {
                          job.then(changed, changed);
                        }}
                      />
                    </div>
                  </div>
                  {group.days.map((day) => (
                    <DayCard
                      key={day.day}
                      trip={id}
                      day={day}
                      sky={skyOn(weather, day)}
                      today={day.day === today}
                      onChanged={changed}
                    />
                  ))}
                </section>
              );
            })}
          </div>
          <div className="journal-side">
            {data.points.length > 0 && (
              <section className="panel journal-map" aria-label={t('journal.mapTitle')}>
                <div className="panel-head">
                  <h2>{t('journal.mapTitle')}</h2>
                </div>
                <MapPanel data={data} label={t('journal.mapTitle')} thin closest={15} />
              </section>
            )}
            <section className="panel" aria-label={t('journal.howTitle')}>
              <div className="panel-head">
                <h2>{t('journal.howTitle')}</h2>
              </div>
              <ol className="panel-body journal-how">
                <li>{t('journal.how1')}</li>
                <li>{t('journal.how2')}</li>
                <li>{t('journal.how3')}</li>
              </ol>
              <p className="small quiet journal-note">{t('journal.howNote')}</p>
            </section>
            <ShareAndBook trip={id} />
          </div>
        </div>
      )}
    </div>
  );
}
